import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { OfflineFallbackNotice } from "./OfflineFallbackNotice";
import { TablePagination } from "./TablePagination";
import { useSyncChanges } from "../../hooks/useSyncEngine";
import { createResource, deleteResource, expectedVersionHeaders, extractErrorMessage, fetchResourceList, updateResource } from "../../lib/api";
import { clearDraftState, loadDraftState, loadOfflineSnapshot, saveDraftState, saveOfflineSnapshot } from "../../lib/offlineState";

const DEFAULT_PAGE_SIZE = 30;
const MANAGEMENT_CACHE_TTL_MS = 30 * 60 * 1000;
const SYNC_ENTITY_BY_ENDPOINT = { "/drivers": "driver", "/trucks": "truck", "/trailers": "trailer", "/customers": "customer", "/locations": "location" };

const buildInitialState = (fields) =>
  fields.reduce((accumulator, field) => ({ ...accumulator, [field.name]: field.defaultValue ?? "" }), {});

const buildManagementCacheKey = ({ currentPage, endpoint, pageSize, search, serverPagination }) => `lumen-tms-management-v1:${JSON.stringify({ endpoint, search, page: serverPagination ? currentPage : 1, pageSize: serverPagination ? pageSize : "all" })}`;

const serializeManagementDraft = (draft) => JSON.stringify(draft || {});

const formatFieldValue = (item, fieldName) => {
  const value = item[fieldName];
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return value;
};

const isEmptySortValue = (value) => value === null || value === undefined || value === "";

const sortIndicator = (active, direction) => (active ? (direction === "asc" ? "↑" : "↓") : "↕");

export const ManagementPage = ({ title, description, endpoint, entityName, actionLabel, fields, columns, note, serverPagination = false, pageTestId = null }) => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formState, setFormState] = useState(buildInitialState(fields));
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortState, setSortState] = useState({ key: null, dir: "asc" });
  const restoredDraftRef = useRef(false);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(serializeManagementDraft(buildInitialState(fields)));
  const [cacheFallbackActive, setCacheFallbackActive] = useState(false);
  const draftStorageKey = useMemo(() => `management-page:${endpoint}`, [endpoint]);
  const managementCacheKey = useMemo(() => buildManagementCacheKey({ currentPage, endpoint, pageSize, search, serverPagination }), [currentPage, endpoint, pageSize, search, serverPagination]);
  const currentFormSnapshot = useMemo(() => serializeManagementDraft(formState), [formState]);
  const hasUnsavedDraft = dialogOpen && currentFormSnapshot !== initialFormSnapshot;

  const fetchItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    const cachedPayload = await loadOfflineSnapshot(managementCacheKey, MANAGEMENT_CACHE_TTL_MS);
    if (cachedPayload) {
      setItems(cachedPayload.items || []);
      setTotalItems(cachedPayload.totalItems || 0);
    }
    try {
      const params = search ? { search } : {};
      const payload = await fetchResourceList(endpoint, serverPagination ? { ...params, page: currentPage, page_size: pageSize } : params);
      let nextItems;
      let nextTotalItems;
      if (serverPagination && !Array.isArray(payload)) {
        nextItems = payload.items || [];
        nextTotalItems = payload.total_items || 0;
      } else {
        nextItems = Array.isArray(payload) ? payload : payload.items || [];
        nextTotalItems = nextItems.length;
      }
      setItems(nextItems);
      setTotalItems(nextTotalItems);
      setCacheFallbackActive(false);
      await saveOfflineSnapshot(managementCacheKey, { items: nextItems, totalItems: nextTotalItems });
    } catch (error) {
      if (cachedPayload) {
        setItems(cachedPayload.items || []);
        setTotalItems(cachedPayload.totalItems || 0);
        setCacheFallbackActive(true);
        toast.warning(`Showing cached ${entityName.toLowerCase()} while the connection recovers.`);
      } else {
        toast.error(extractErrorMessage(error, `Unable to load ${entityName.toLowerCase()} right now.`));
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, endpoint, entityName, managementCacheKey, pageSize, search, serverPagination]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useSyncChanges([SYNC_ENTITY_BY_ENDPOINT[endpoint] || endpoint], () => fetchItems({ silent: true }));

  useEffect(() => {
    if (restoredDraftRef.current || dialogOpen) {
      return;
    }
    const storedDraft = loadDraftState(draftStorageKey);
    if (!storedDraft?.formState) {
      return;
    }
    restoredDraftRef.current = true;
    setEditingItem(storedDraft.editingItem || null);
    setFormState(storedDraft.formState);
    setInitialFormSnapshot(storedDraft.baseSnapshot || serializeManagementDraft(buildInitialState(fields)));
    setDialogOpen(true);
    toast.info(storedDraft.editingItem ? `Restored an unsaved ${entityName.toLowerCase()} draft.` : `Restored your unsaved new ${entityName.toLowerCase()} draft.`);
  }, [dialogOpen, draftStorageKey, entityName, fields]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    if (!hasUnsavedDraft) {
      clearDraftState(draftStorageKey);
      return;
    }
    saveDraftState(draftStorageKey, {
      baseSnapshot: initialFormSnapshot,
      editingItem,
      formState,
    });
  }, [dialogOpen, draftStorageKey, editingItem, formState, hasUnsavedDraft, initialFormSnapshot]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((serverPagination ? totalItems : items.length) / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, items.length, pageSize, serverPagination, totalItems]);

  const openCreateDialog = () => {
    const nextFormState = buildInitialState(fields);
    clearDraftState(draftStorageKey);
    setEditingItem(null);
    setFormState(nextFormState);
    setInitialFormSnapshot(serializeManagementDraft(nextFormState));
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    const nextFormState = fields.reduce(
      (accumulator, field) => ({
        ...accumulator,
        [field.name]: typeof item[field.name] === "boolean" ? String(item[field.name]) : item[field.name] ?? field.defaultValue ?? "",
      }),
      {},
    );
    clearDraftState(draftStorageKey);
    setEditingItem(item);
    setFormState(nextFormState);
    setInitialFormSnapshot(serializeManagementDraft(nextFormState));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    const nextFormState = buildInitialState(fields);
    setFormState(nextFormState);
    setInitialFormSnapshot(serializeManagementDraft(nextFormState));
    clearDraftState(draftStorageKey);
  };

  const handleChange = (event) => {
    setFormState((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const cleanPayload = useMemo(
    () => (payload) => {
      const trimmedPayload = Object.fromEntries(
        Object.entries(payload).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]),
      );
      return fields.reduce((accumulator, field) => {
        const value = trimmedPayload[field.name];
        if (field.parse) {
          accumulator[field.name] = field.parse(value);
        } else if (value === "" && !field.required) {
          accumulator[field.name] = null;
        } else {
          accumulator[field.name] = value;
        }
        return accumulator;
      }, {});
    },
    [fields],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = cleanPayload(formState);
      if (editingItem) {
        await updateResource(endpoint, editingItem.id, payload, expectedVersionHeaders(editingItem));
        toast.success(`${entityName} updated.`);
      } else {
        await createResource(endpoint, payload);
        toast.success(`${entityName} added.`);
      }
      clearDraftState(draftStorageKey);
      closeDialog();
      fetchItems();
    } catch (error) {
      if (error?.response?.status === 409) {
        fetchItems();
      }
      toast.error(extractErrorMessage(error, `Unable to save ${entityName.toLowerCase()}.`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(`Delete this ${entityName.toLowerCase()}?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteResource(endpoint, item.id);
      toast.success(`${entityName} deleted.`);
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to delete ${entityName.toLowerCase()}.`));
    }
  };

  const sortedItems = useMemo(() => {
    if (serverPagination || !sortState.key) {
      return items;
    }
    const factor = sortState.dir === "asc" ? 1 : -1;
    return [...items].sort((rowA, rowB) => {
      const a = rowA[sortState.key];
      const b = rowB[sortState.key];
      const aEmpty = isEmptySortValue(a);
      const bEmpty = isEmptySortValue(b);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return factor * String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
    });
  }, [items, serverPagination, sortState]);

  const visibleItems = useMemo(() => (serverPagination ? items : sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)), [sortedItems, items, currentPage, pageSize, serverPagination]);

  const toggleSort = (key) => {
    setSortState((current) => (current.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6" data-testid={pageTestId || `${entityName.toLowerCase()}-page`}>
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Master records</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">{title}</h1>
        </div>

        <button className="inline-flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800" data-testid={`${entityName.toLowerCase()}-create-button`} onClick={openCreateDialog} type="button">
          <Plus className="h-4 w-4" />
          {actionLabel}
        </button>
      </section>
      <OfflineFallbackNotice message={cacheFallbackActive ? `Showing cached ${entityName.toLowerCase()} while the connection recovers.` : ""} testId={`${entityName.toLowerCase()}-offline-fallback-notice`} />
      <section className="table-shell" data-testid={`${entityName.toLowerCase()}-table-section`}>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid={`${entityName.toLowerCase()}-table`}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key}>
                    {serverPagination ? (
                      column.label
                    ) : (
                      <button className="inline-flex items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500 transition hover:text-zinc-900" data-testid={`${entityName.toLowerCase()}-sort-header-${column.key}`} onClick={() => toggleSort(column.key)} type="button">
                        <span>{column.label}</span>
                        <span aria-hidden="true">{sortIndicator(sortState.key === column.key, sortState.dir)}</span>
                      </button>
                    )}
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={columns.length + 1} data-testid={`${entityName.toLowerCase()}-loading-state`}>
                    Loading {entityName.toLowerCase()}...
                  </td>
                </tr>
              ) : totalItems === 0 ? (
                <tr>
                  <td className="py-10 text-center text-sm text-zinc-500" colSpan={columns.length + 1} data-testid={`${entityName.toLowerCase()}-empty-state`}>
                    No {entityName.toLowerCase()} found for the current search.
                  </td>
                </tr>
              ) : (
                visibleItems.map((item) => (
                  <tr key={item.id} data-testid={`${entityName.toLowerCase()}-row-${item.id}`}>
                    {columns.map((column) => (
                      <td key={`${item.id}-${column.key}`} data-testid={`${entityName.toLowerCase()}-${column.key}-${item.id}`}>
                        {column.render ? column.render(item, Link) : formatFieldValue(item, column.key)}
                      </td>
                    ))}
                    <td>
                      <div className="flex flex-wrap gap-2">
                        <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`${entityName.toLowerCase()}-edit-${item.id}`} onClick={() => openEditDialog(item)} type="button">
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-red-600 transition hover:bg-red-50" data-testid={`${entityName.toLowerCase()}-delete-${item.id}`} onClick={() => handleDelete(item)} type="button">
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination currentPage={currentPage} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} testIdPrefix={`${entityName.toLowerCase()}-table`} totalItems={totalItems} />
      </section>

      {dialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6" data-testid={`${entityName.toLowerCase()}-dialog-overlay`}>
          <div className="panel-surface w-full max-w-3xl px-6 py-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{editingItem ? "Edit record" : "New record"}</p>
                <h2 className="mt-2 text-2xl font-semibold text-zinc-900">{editingItem ? `Edit ${entityName}` : actionLabel}</h2>
              </div>
              <button className="rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`${entityName.toLowerCase()}-dialog-close-button`} onClick={closeDialog} type="button">
                Close
              </button>
            </div>

            <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={handleSubmit} data-testid={`${entityName.toLowerCase()}-dialog-form`}>
              {fields.map((field) => (
                <label className={`${field.type === "textarea" ? "md:col-span-2" : ""} block space-y-2`} key={field.name}>
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{field.label}</span>
                  {field.type === "select" ? (
                    <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10" data-testid={`${entityName.toLowerCase()}-field-${field.name}`} name={field.name} onChange={handleChange} required={field.required} value={formState[field.name]}>
                      <option value="">Select</option>
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea className="min-h-[120px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10" data-testid={`${entityName.toLowerCase()}-field-${field.name}`} name={field.name} onChange={handleChange} placeholder={field.placeholder} required={field.required} value={formState[field.name]} />
                  ) : (
                    <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10" data-testid={`${entityName.toLowerCase()}-field-${field.name}`} min={field.min} name={field.name} onChange={handleChange} placeholder={field.placeholder} required={field.required} step={field.step} type={field.type || "text"} value={formState[field.name]} />
                  )}
                </label>
              ))}

              <div className="flex flex-wrap justify-end gap-3 md:col-span-2">
                <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`${entityName.toLowerCase()}-dialog-cancel-button`} onClick={closeDialog} type="button">
                  Cancel
                </button>
                <button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid={`${entityName.toLowerCase()}-dialog-save-button`} disabled={saving} type="submit">
                  {saving ? "Saving..." : editingItem ? `Save ${entityName}` : actionLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};